/**
 * TUVI - Detalle Completo del Proyecto
 * Vista principal con navegación por capítulos
 */

import React, { useState, useEffect, useCallback } from 'react';
import BitacoraProyecto from './Detalle/BitacoraProyecto';
import './Detalle/BitacoraProyecto.css';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import ResumenProyecto from './Detalle/ResumenProyecto';
import InformacionBasica from './Detalle/InformacionBasica';
import CapituloTecnico from './Detalle/CapituloTecnico';
import CronogramaProyecto from './Detalle/CronogramaProyecto';
// FacturasProyecto y CajaMenorProyecto removidos - ahora son módulos independientes
import ActividadesCapitulo from './Detalle/ActividadesCapitulo';
import AdicionalesProyecto from './Detalle/AdicionalesProyecto';
import ActualizacionesProyecto from './Detalle/ActualizacionesProyecto';
import FichaFfeProyecto from './Detalle/FichaFfeProyecto';
import ActaEntrega from './Detalle/ActaEntrega';
import PazYSalvo from './Detalle/PazYSalvo';
import AdjuntosProyectoTab from './Detalle/AdjuntosProyectoTab';
import ControlPresupuestal from './Detalle/ControlPresupuestal';
import CarteraAjustesProyecto from './Detalle/CarteraAjustesProyecto';
import ForoProyecto from './Detalle/ForoProyecto';
import HistorialEstados from './Detalle/HistorialEstados';
import UbicacionProyecto from './Detalle/UbicacionProyecto';
import './DetalleProyecto.css';

const DetalleProyecto = ({ proyectoId, onVolver, onVerPosventa }) => {
  const { success, error: showError } = useNotification();
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capituloActivo, setCapituloActivo] = useState('resumen');
  const [proveedores, setProveedores] = useState([]);
  const [verActividadesDeCapitulo, setVerActividadesDeCapitulo] = useState(null);
  const [foroNoLeidos, setForoNoLeidos] = useState(0);
  // Actualizaciones pendientes de firma del cliente: badge de la pestaña
  const [actualizacionesPendientes, setActualizacionesPendientes] = useState(0);
  const cargarActualizacionesPendientes = useCallback(async () => {
    try {
      const d = await client.query('query($p: Int!) { actualizacionesProyecto(proyecto_id: $p) { estado } }', { p: proyectoId });
      setActualizacionesPendientes((d.actualizacionesProyecto || []).filter(a => a.estado === 'pendiente').length);
    } catch (e) { /* módulo aún no desplegado */ }
  }, [proyectoId]);
  useEffect(() => { cargarActualizacionesPendientes(); }, [cargarActualizacionesPendientes]);
  // Novedades del proyecto: alimenta el badge de la pestaña y el aviso del Resumen
  // (resumen + los compromisos vigentes con fecha, compensación y detalle).
  const [bitacora, setBitacora] = useState({ vencidos: 0, proximos: 0, pendientes: 0, cumplidos: 0, lista: [] });

  const cargarBitacora = useCallback(async () => {
    if (!proyectoId) return;
    try {
      const d = await client.query(
        `query($p: Int!) { bitacoraProyecto(proyecto_id: $p) {
          resumen { vencidos proximos pendientes cumplidos total }
          compromisos { id descripcion fecha_entrega valor_compensatorio compensatorio negociador estado semaforo dias_para_vencer cerrado_at }
        } }`,
        { p: parseInt(proyectoId) });
      const b = d.bitacoraProyecto;
      if (b?.resumen) {
        // El aviso del Resumen es PERMANENTE: muestra los compromisos vigentes y
        // también los ya cumplidos. Los cancelados fueron anulados y no se muestran.
        setBitacora({
          ...b.resumen,
          lista: (b.compromisos || []).filter(c => c.estado !== 'cancelado'),
        });
      }
    } catch { /* si falla, la pestaña sigue funcionando sin badge */ }
  }, [proyectoId]);

  const fmtFechaNov = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Sin fecha';
  const textoDiasNov = (d) => {
    if (d == null) return '';
    if (d < 0) return `vencido hace ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}`;
    if (d === 0) return 'vence hoy';
    if (d === 1) return 'vence mañana';
    return `vence en ${d} días`;
  };

  useEffect(() => { cargarBitacora(); }, [cargarBitacora]);
  // null = cargando; true/false = tiene/no tiene Ficha FFE asociada.
  // La pestaña "Adjuntos" (a nivel proyecto) solo aparece cuando NO hay Ficha FFE
  // (los proyectos con Ficha FFE ya tienen su propia pestaña Adjuntos adentro).
  const [tieneFichaFfe, setTieneFichaFfe] = useState(null);

  // Contador de respuestas no leídas del foro (badge en la pestaña)
  const cargarForoNoLeidos = React.useCallback(async () => {
    if (!proyectoId) return;
    try {
      const data = await client.query(`query($pid: Int!) { foroNoLeidos(proyecto_id: $pid) }`, { pid: proyectoId });
      setForoNoLeidos(data.foroNoLeidos || 0);
    } catch { /* silencioso */ }
  }, [proyectoId]);

  useEffect(() => { cargarForoNoLeidos(); }, [cargarForoNoLeidos]);

  // Detectar si el proyecto tiene Ficha FFE (misma query que FichaFfeProyecto)
  useEffect(() => {
    if (!proyectoId) return;
    let activo = true;
    client.query(`query($pid: Int!) { fichaFfeProyecto(proyecto_id: $pid) { cotizacion_id } }`, { pid: proyectoId })
      .then(d => { if (activo) setTieneFichaFfe(!!d.fichaFfeProyecto); })
      .catch(() => { if (activo) setTieneFichaFfe(false); });
    return () => { activo = false; };
  }, [proyectoId]);
  // Al entrar al foro, se marca visto → resetear badge localmente
  useEffect(() => { if (capituloActivo === 'foro') setForoNoLeidos(0); }, [capituloActivo]);

  useEffect(() => {
    cargarProyecto();
    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectoId]);

  const cargarProveedores = async () => {
    try {
      const data = await client.query(`{
        proveedores { proveedores { id nombre tipo clasificacion nit contacto_nombre telefono email } }
      }`);
      setProveedores(data.proveedores.proveedores || []);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarProyecto = async () => {
    try {
      setLoading(true);
      const data = await client.query(`
        query($id: Int!) {
          proyecto(id: $id) {
            proyecto {
              id nombre_cliente cedula telefono correo direccion
              proyecto_nombre area_m2 habitaciones banos tipo_cocina
              apartamento negocio ubicacion_detallada fecha_cotizacion
              comercial residente contratista fecha_entrega_estimada
              fecha_inicio_cronograma fecha_entrega_programada fecha_entrega_real
              subtotal aiu iva valor_final valor_ejecutado estado
              progreso_general cotizacion_id latitud longitud radio_metros
              deposito_admon fecha_deposito_admon devolucion_deposito
              fecha_devolucion_deposito llaves_entregadas fecha_entrega_llaves
              pago_inicial fecha_pago_inicial anticipo_carpinteria
              fecha_anticipo_carpinteria liquidacion fecha_liquidacion
              dias_retraso notas_generales motivo_retrasos
              monto_pagado monto_pendiente gasto_manual valor_ajustado
            }
            capitulos {
              id proyecto_id numero capitulo_nombre subtotal costo_total
              porcentaje fecha_inicio_cro fecha_fin_cro fecha_inicio fecha_fin
              progreso estado estado_calculado notas responsable
              contratista_id contratista_nombre contratista_nit
              dias_retraso_fin dias_retraso_inicio dias_vencido
              valor_actividades valor_ejecutado_actividades
              total_facturas_capitulo total_facturado_capitulo
              valor_ejecutado_total porcentaje_ejecucion porcentaje_ejecucion_total
              excede_presupuesto caja_menor salidas_bodega total_gastado
              actividades { id descripcion valor orden }
              total_actividades
            }
            pagos {
              id proyecto_id nombre monto porcentaje estado
              fecha_programada fecha_pago comprobante_archivo comprobante_ruta
            }
            archivos {
              id proyecto_id nombre_archivo ruta tipo tamano_bytes fecha_subida
            }
            estadisticas {
              total_capitulos capitulos_completados capitulos_en_progreso
              capitulos_retrasados progreso_porcentaje total_actividades
              total_pagos pagos_realizados pagos_pendientes total_archivos
              total_caja_menor cantidad_gastos_caja_menor
              total_salidas_bodega cantidad_salidas_bodega
              total_posventa cantidad_posventa
              total_facturas_sin_capitulo cantidad_facturas_sin_capitulo
              vf_total_ejecutado vf_facturas_pagadas vf_facturas_montadas vf_gasto_manual vf_caja_menor vf_salidas_bodega
              total_reintegros cantidad_reintegros
              total_descuentos_cartera cantidad_descuentos_cartera
            }
          }
        }
      `, { id: proyectoId });

      if (data.proyecto) {
        setProyecto(data.proyecto);
      } else {
        throw new Error('Proyecto no encontrado');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Número del capítulo como identificador visual
  const getNumeroCapitulo = (index) => {
    return index + 1;
  };

  // Formatear nombre de capítulo a Title Case para uniformidad
  const formatearNombreCapitulo = (nombre) => {
    if (!nombre) return '';
    // Palabras que deben permanecer en minúsculas (excepto al inicio)
    const minusculas = ['y', 'de', 'del', 'e', 'en', 'con', 'para', 'por'];

    return nombre
      .toLowerCase()
      .split(' ')
      .map((palabra, index) => {
        // Mantener conectores en minúsculas excepto al inicio
        if (index > 0 && minusculas.includes(palabra)) {
          return palabra;
        }
        return palabra.charAt(0).toUpperCase() + palabra.slice(1);
      })
      .join(' ');
  };

  // Generar capítulos dinámicamente desde los datos del proyecto
  const capitulos = [
    { id: 'resumen', nombre: 'Resumen', tipo: 'especial' },
    { id: 'informacion_basica', nombre: 'Información', tipo: 'especial' },
    { id: 'ubicacion', nombre: 'Ubicación', tipo: 'especial', badge: proyecto?.proyecto?.latitud == null ? '!' : undefined },
    // Agregar capítulos dinámicos del proyecto
    ...(proyecto?.capitulos || []).map((cap, index) => ({
      id: cap.id,
      nombre: formatearNombreCapitulo(cap.capitulo_nombre),
      numero: getNumeroCapitulo(index),
      tipo: 'tecnico',
      capituloData: cap
    })),
    { id: 'cronograma', nombre: 'Cronograma', tipo: 'especial' },
    { id: 'adicionales', nombre: 'Adicionales', tipo: 'especial' },
    { id: 'actualizaciones', nombre: 'Actualizaciones', tipo: 'especial', badge: actualizacionesPendientes || undefined },
    { id: 'ficha-ffe', nombre: 'Ficha FFE', tipo: 'especial' },
    // Adjuntos a nivel proyecto: solo si NO tiene Ficha FFE (los que sí la
    // tienen usan la pestaña Adjuntos dentro de la propia Ficha FFE).
    ...(tieneFichaFfe === false ? [{ id: 'adjuntos-proyecto', nombre: 'Adjuntos', tipo: 'especial' }] : []),
    { id: 'control-presupuestal', nombre: 'Control Presupuestal', tipo: 'especial' },
    { id: 'reintegros-descuentos', nombre: 'Reintegros y Descuentos', tipo: 'especial' },
    { id: 'acta-entrega', nombre: 'Acta de Entrega', tipo: 'especial' },
    { id: 'paz-y-salvo', nombre: 'Paz y Salvo', tipo: 'especial' },
    // Historial de estados: siempre visible, sin importar el estado del proyecto.
    { id: 'historial-estados', nombre: 'Historial de Estados', tipo: 'especial' },
    { id: 'bitacora', nombre: 'Novedades', tipo: 'especial', badge: bitacora.vencidos },
    { id: 'foro', nombre: 'Foro', tipo: 'especial', badge: foroNoLeidos }
  ];

  // Actualizar capítulo en el backend
  const handleUpdateCapitulo = async (capituloId, datos) => {
    try {
      const data = await client.mutate(`
        mutation($capitulo_id: Int!, $input: ActualizarCapituloInput!) {
          actualizarCapitulo(capitulo_id: $capitulo_id, input: $input) { success message }
        }
      `, { capitulo_id: capituloId, input: datos });

      if (data.actualizarCapitulo.success) {
        cargarProyecto();
        success('Capítulo actualizado correctamente');
      } else {
        throw new Error(data.actualizarCapitulo.message || 'Error al actualizar capítulo');
      }
    } catch (err) {
      console.error('Error:', err);
      showError('Error al actualizar capítulo: ' + err.message);
    }
  };

  // Aviso de novedades: permanente, visible en TODAS las pestañas del proyecto
  // desde que se registra el primer compromiso (también los ya cumplidos).
  const renderAvisoNovedades = () => {
    if (!(bitacora.lista || []).length) return null;
    const enNovedades = capituloActivo === 'bitacora';
    return (
            <div
              className={`bit-aviso rojo bit-aviso-grande bit-aviso-bloque ${enNovedades ? 'en-novedades' : ''}`}
              role="button" tabIndex={0}
              onClick={() => { if (!enNovedades) setCapituloActivo('bitacora'); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !enNovedades) setCapituloActivo('bitacora'); }}
            >
              <div className="bit-aviso-cabecera">
                <span className="bit-aviso-icono">⚠</span>
                <span className="bit-aviso-txt">
                  <strong>
                    {`Este proyecto tiene ${bitacora.lista.length} compromiso${bitacora.lista.length === 1 ? '' : 's'} pactado${bitacora.lista.length === 1 ? '' : 's'} con el cliente`}
                  </strong>
                  <small>
                    {bitacora.vencidos > 0 && `${bitacora.vencidos} vencido${bitacora.vencidos === 1 ? '' : 's'} · `}
                    {bitacora.proximos > 0 && `${bitacora.proximos} por vencer · `}
                    {bitacora.cumplidos > 0 && `${bitacora.cumplidos} cumplido${bitacora.cumplidos === 1 ? '' : 's'}${enNovedades ? '' : ' · '}`}
                    {!enNovedades && 'Clic para ver las novedades del proyecto'}
                  </small>
                </span>
                {!enNovedades && <span className="bit-aviso-flecha">Ver novedades →</span>}
              </div>
              <div className="bit-aviso-lista">
                {bitacora.lista.map(c => (
                  <div key={c.id} className={`bit-aviso-fila ${c.estado === 'cumplido' ? 'cumplida' : (c.semaforo === 'vencido' ? 'vencida' : '')}`}>
                    <span className="bit-aviso-dato">
                      <span className="bit-aviso-etq">Nueva fecha de entrega</span>
                      <b>{fmtFechaNov(c.fecha_entrega)}</b>
                      {c.estado === 'cumplido'
                        ? <em className="bit-aviso-cumplido">✓ Cumplido{c.cerrado_at ? ` el ${new Date(c.cerrado_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}` : ''}</em>
                        : (textoDiasNov(c.dias_para_vencer) && <em> · {textoDiasNov(c.dias_para_vencer)}</em>)}
                    </span>
                    <span className="bit-aviso-dato">
                      <span className="bit-aviso-etq">Compensatorio</span>
                      <b>{c.compensatorio || (c.valor_compensatorio > 0 ? '$ ' + Math.round(c.valor_compensatorio).toLocaleString('es-CO') : 'Sin compensatorio')}</b>
                    </span>
                    <span className="bit-aviso-dato bit-aviso-detalle">
                      <span className="bit-aviso-etq">Detalle</span>
                      <b>{c.descripcion}</b>
                      {c.negociador && <em> · negoció {c.negociador}</em>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
    );
  };

  // Renderizar contenido según capítulo activo
  const renderContenido = () => {
    // Módulos especiales
    if (capituloActivo === 'resumen') {
      return <ResumenProyecto proyecto={proyecto} onVerPosventa={onVerPosventa} />;
    }

    if (capituloActivo === 'informacion_basica') {
      return <InformacionBasica proyecto={proyecto} onProyectoActualizado={cargarProyecto} />;
    }

    if (capituloActivo === 'ubicacion') {
      return <UbicacionProyecto proyecto={proyecto?.proyecto} onProyectoActualizado={cargarProyecto} />;
    }

    if (capituloActivo === 'cronograma') {
      return <CronogramaProyecto proyectoId={proyectoId} />;
    }

    if (capituloActivo === 'actualizaciones') {
      return <ActualizacionesProyecto proyectoId={proyectoId} proyecto={proyecto} onCambio={cargarActualizacionesPendientes} />;
    }

    if (capituloActivo === 'adicionales') {
      return <AdicionalesProyecto proyectoId={proyectoId} proyecto={proyecto} onProyectoActualizado={cargarProyecto} />;
    }

    if (capituloActivo === 'ficha-ffe') {
      return <FichaFfeProyecto proyectoId={proyectoId} />;
    }

    if (capituloActivo === 'control-presupuestal') {
      return <ControlPresupuestal proyectoId={proyectoId} />;
    }

    if (capituloActivo === 'reintegros-descuentos') {
      return <CarteraAjustesProyecto proyectoId={proyectoId} proyecto={proyecto} onProyectoActualizado={cargarProyecto} />;
    }

    if (capituloActivo === 'acta-entrega') {
      return <ActaEntrega proyecto={proyecto} />;
    }

    if (capituloActivo === 'paz-y-salvo') {
      return <PazYSalvo proyecto={proyecto} />;
    }

    if (capituloActivo === 'adjuntos-proyecto') {
      return <AdjuntosProyectoTab proyectoId={proyectoId} />;
    }

    if (capituloActivo === 'historial-estados') {
      return <HistorialEstados proyectoId={proyectoId} />;
    }

    if (capituloActivo === 'bitacora') {
      return <BitacoraProyecto proyectoId={proyectoId} onCambio={cargarBitacora} />;
    }

    if (capituloActivo === 'foro') {
      return <ForoProyecto proyectoId={proyectoId} />;
    }

    // Capítulos técnicos (usando ID numérico del capítulo)
    const capituloData = proyecto.capitulos.find(c => c.id === capituloActivo);

    if (capituloData) {
      // Si estamos viendo actividades de este capítulo
      if (verActividadesDeCapitulo && verActividadesDeCapitulo === capituloData.id) {
        return (
          <ActividadesCapitulo
            proyectoId={proyectoId}
            capitulo={capituloData}
            proveedores={proveedores}
            onVolver={() => setVerActividadesDeCapitulo(null)}
            onProyectoActualizado={cargarProyecto}
          />
        );
      }

      // Vista normal del capítulo
      return (
        <CapituloTecnico
          key={capituloData.id}
          capitulo={capituloData}
          proyectoId={proyectoId}
          onUpdate={handleUpdateCapitulo}
          onVerActividades={() => setVerActividadesDeCapitulo(capituloData.id)}
        />
      );
    }

    // Fallback
    return null;
  };

  if (loading) {
    return (
      <div className="detalle-proyecto-container">
        <div className="detalle-loading">
          <div className="construction-loader">
            <div className="loader-bricks">
              <div className="loader-brick"></div>
              <div className="loader-brick"></div>
              <div className="loader-brick"></div>
            </div>
          </div>
          <p>Cargando detalles del proyecto...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detalle-proyecto-container">
        <div className="error-message">
          <h3>Error al cargar proyecto</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={cargarProyecto} className="btn-retry">
              Reintentar
            </button>
            <button onClick={onVolver} className="btn-volver">
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!proyecto) {
    return (
      <div className="detalle-proyecto-container">
        <div className="error-message">
          <h3>Proyecto no encontrado</h3>
          <button onClick={onVolver} className="btn-volver">
            Volver a la lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detalle-proyecto-container">
      {/* Header con info del proyecto */}
      <div className="detalle-header">
        <button onClick={onVolver} className="btn-back">
          Volver
        </button>
        <div className="proyecto-title-section">
          <h1>{proyecto.proyecto.proyecto_nombre}</h1>
          <div className="proyecto-meta">
            <span className="meta-item">
              {proyecto.proyecto.nombre_cliente}
            </span>
            <span className="meta-item">
              {proyecto.proyecto.apartamento || 'Sin apartamento'}
            </span>
            <span className="meta-item">
              {proyecto.proyecto.area_m2} m²
            </span>
            <span className={`estado-badge ${proyecto.proyecto.estado}`}>
              {proyecto.proyecto.estado.replace('_', ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Layout Principal: Sidebar + Contenido */}
      <div className="detalle-layout">
        {/* Sidebar de Navegación */}
        <aside className="detalle-sidebar">
          <div className="sidebar-title">
            <h3>Navegación</h3>
          </div>
          <nav className="capitulos-nav">
            {capitulos.map((capitulo) => {
              return (
                <button
                  key={capitulo.id}
                  className={`capitulo-btn ${capituloActivo === capitulo.id ? 'active' : ''} ${capitulo.capituloData?.excede_presupuesto ? 'excede-presupuesto' : ''}`}
                  onClick={() => setCapituloActivo(capitulo.id)}
                >
                  {capitulo.numero && (
                    <span className="capitulo-icono">{capitulo.numero}</span>
                  )}
                  <span className="capitulo-nombre">{capitulo.nombre}</span>
                  {capitulo.badge > 0 && (
                    <span className="foro-nav-badge">{capitulo.badge}</span>
                  )}
                  {capitulo.capituloData && capitulo.capituloData.porcentaje > 0 && (
                    <span className={`capitulo-porcentaje ${capitulo.capituloData.excede_presupuesto ? 'excede' : ''}`}>
                      {capitulo.capituloData.porcentaje.toFixed(0)}%
                    </span>
                  )}
                  {capitulo.capituloData && (
                    <span className={`capitulo-estado ${capitulo.capituloData.estado_calculado} ${capitulo.capituloData.excede_presupuesto ? 'excede' : ''}`}>
                      {capitulo.capituloData.excede_presupuesto && '!'}
                      {!capitulo.capituloData.excede_presupuesto && capitulo.capituloData.estado_calculado === 'completado' && 'OK'}
                      {!capitulo.capituloData.excede_presupuesto && capitulo.capituloData.estado_calculado === 'en_progreso' && '...'}
                      {!capitulo.capituloData.excede_presupuesto && capitulo.capituloData.estado_calculado === 'retrasado' && '!'}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenido Principal */}
        <main className="detalle-content">
          {renderAvisoNovedades()}
          {renderContenido()}
        </main>
      </div>
    </div>
  );
};

export default DetalleProyecto;
